/**
 * WeatherBarPresenter - Stateless presenter for weather forecast bars
 *
 * NOTE: This presenter component is separated from WeatherBar.tsx for Module Federation.
 * It contains the same UI logic but receives all data via props instead of MobX injection.
 * This allows JHP to consume it via Module Federation while Zoomin's WeatherBar.tsx
 * continues to use MobX stores for data. Both components should render identically.
 */

import * as React from 'react'

import OpenWeatherIcon from '~/client/src/shared/components/OpenWeatherIcon'
import * as WeatherIcon from '~/client/src/shared/components/WeatherIcon'

const MIN_PRECIPITATION_THRESHOLD = 0.1
const ICON_SIZE = 10
const DEGREE_SYMBOL = '°'
const DEFAULT_BAR_WIDTH_CLASS = 'w48'

export interface IWeatherBarForecast {
  /** Unix timestamp (ms) - used as `time` for hourly or `date` for daily */
  time?: number
  date?: number
  temperature: number
  weatherIconCode: string
  windSpeed: number
  windDegree: number
  probabilityOfPrecipitation: number
  amountOfPrecipitation: number
}

export interface IWeatherBarPresenterProps {
  forecast: IWeatherBarForecast
  units: 'imperial' | 'metric'
  height: number
  iconSize?: number
  barWidthClass?: string
  renderTimeSection?: (timestamp: number) => JSX.Element
  /** Format time for display - returns [time, period] e.g. ["3", "PM"] */
  formatTime?: (timestamp: number) => [string, string]

  /** Whether it's constrained horizontally */
  compactH?: boolean
  /** Whether it's constrained vertically */
  compactV?: boolean
}

/**
 * Format precipitation value with appropriate precision
 */
function littlePrecision(val: number): string {
  if (val < 0.1) return ''
  if (val < 1) return val.toFixed(1)
  return Math.round(val).toString()
}

/**
 * Convert millimeters to inches
 */
function mmToInch(mm: number): number {
  return mm * 0.0394
}

/**
 * Get wind speed unit label
 */
function windSpeedUnits(units: 'imperial' | 'metric'): string {
  return units === 'imperial' ? 'mph' : 'm/s'
}

export default function WeatherBarPresenter({
  forecast,
  units,
  height,
  iconSize,
  barWidthClass = DEFAULT_BAR_WIDTH_CLASS,
  renderTimeSection,
  formatTime,
  compactH,
}: IWeatherBarPresenterProps): JSX.Element {
  const temperature = forecast.temperature
  const weatherIconCode = forecast.weatherIconCode
  const probabilityOfPrecipitation = forecast.probabilityOfPrecipitation
  const windSpeed = forecast.windSpeed
  const windDegree = forecast.windDegree
  const amountOfPrecipitation = forecast.amountOfPrecipitation

  // Get the timestamp (hourly uses `time`, daily uses `date`)
  const timestamp = forecast.time ?? forecast.date ?? 0

  // Default time section renderer
  const defaultRenderTimeSection = (): JSX.Element => {
    if (formatTime) {
      const [time, period] = formatTime(timestamp)
      return (
        <>
          <span className="text bold">{time}</span>
          <span className="text opacity5 lowercase">{period}</span>
        </>
      )
    }
    // Fallback if no formatTime provided
    return (
      <>
        <span className="text bold">--</span>
        <span className="text opacity5 lowercase"></span>
      </>
    )
  }

  const timeRenderer = renderTimeSection
    ? () => renderTimeSection(timestamp)
    : defaultRenderTimeSection

  return (
    <div className="col x-center y-end">
      <div
        className={`weather-bar col x-center y-end no-grow ba-light-grey brada8 py8 ${barWidthClass}`}
        style={{ minHeight: height }}
      >
        {!!probabilityOfPrecipitation && (
          <span className="text lp05 no-grow ml2 grey-10">
            {Math.round(probabilityOfPrecipitation)}%
          </span>
        )}
        <OpenWeatherIcon
          openWeatherIconCode={weatherIconCode}
          size={iconSize}
          isColor
        />
        <span className="text extra-large bold my8">
          {Math.round(temperature)}
          {DEGREE_SYMBOL}
        </span>
        {timeRenderer()}
      </div>
      {renderWindSection(windSpeed, windDegree, units, compactH)}
      {renderPrecipitationSection(amountOfPrecipitation, units, compactH)}
    </div>
  )
}

function renderWindSection(
  windSpeed: number,
  windDegree: number,
  units: 'imperial' | 'metric',
  compactH?: boolean,
): JSX.Element {
  return (
    <div className="row mt8 mb4">
      <WeatherIcon.WindDirection
        size={ICON_SIZE}
        className="text grey-light"
        style={{ transform: `rotate(${windDegree}deg)` }}
      />
      <span className="text bold lp05 ml2">{Math.round(windSpeed)}</span>
      {!compactH && (
        <span className="text small grey-light lp05">
          {windSpeedUnits(units)}
        </span>
      )}
    </div>
  )
}

function renderPrecipitationSection(
  amountOfPrecipitation: number,
  units: 'imperial' | 'metric',
  compactH?: boolean,
): JSX.Element {
  const isZero =
    !amountOfPrecipitation ||
    amountOfPrecipitation < MIN_PRECIPITATION_THRESHOLD

  return (
    <div className="row mt4 mb8">
      <WeatherIcon.DewPoint className="text grey-light icon-w12" />
      {units === 'imperial' ? (
        <span className="text bold lp05 ml2 no-white-space-wrap">
          {littlePrecision(mmToInch(amountOfPrecipitation)) || '--'}
          {!isZero && !compactH && `"`}
        </span>
      ) : (
        <>
          <span className="text bold lp05 ml2 no-white-space-wrap">
            {littlePrecision(amountOfPrecipitation) || '--'}
          </span>
          <span className="text small grey-light lpMinus05">
            {!isZero && !compactH && `mm`}
          </span>
        </>
      )}
    </div>
  )
}
